import React, { useEffect, useState } from "react";
import { Card, Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import {
  FaSearch,
  FaFilter,
  FaShoppingCart,
  FaStar,
  FaStarHalfAlt,
} from "react-icons/fa";
import Axios from "../../../axios/axios";

import { Footer } from "../../../components/SpearParts/Layouts/Footer";
import { Link } from "react-router-dom";
import LoadingPage from "../../Loader/LoadingPage";
import { Header } from "./Header";
import TopNavBar from "./TopNavBar";
import TopSearchBar from "./TopSearchBar";
import { Helmet } from "react-helmet-async";

function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Track filter changes separately
  const [filtersChanged, setFiltersChanged] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const businessType = sessionStorage.getItem("businessType");

    const fetchProductsAndFilters = async () => {
      let url = "";
      let typeKey = "";

      if (businessType === "Auto-Spare") {
        url = "/spareproducts";
        typeKey = "spare";
      } else if (businessType === "Engineering") {
        url = "/engproducts";
        typeKey = "engineering";
      } else if (businessType === "Building") {
        url = "/bldproducts";
        typeKey = "building";
      } else if (businessType === "Rental") {
        url = "/rentalproducts";
        typeKey = "rental";
      } else {
        setError("Invalid or missing business type.");
        setLoading(false);
        setIsLoading(false);
        return;
      }

      try {
        const [productRes, typeDetailsRes] = await Promise.all([
          Axios.get(
            `${url}?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`
          ),
          Axios.get("/type-based-details"),
        ]);

        // Handle paginated response
        const responseData = productRes.data || {};
        const data = responseData.data || [];
        const paginationData = responseData.pagination || {
          totalPages: 1,
          totalItems: 0,
        };

        // Normalize products
        const normalized = data.map((item) => ({
          id: item._id,
          name:
            item.partName ||
            item.name ||
            item.meterialName ||
            item.rentalItem ||
            "Unnamed",
          images: item.images || item.photos || [],
          brand: item.brandId?._id || item.brandId || "",
          model: item.modelId?._id || item.modelId || "",
          partNo: item.partNo,
          year: item.year,
          engine: item.engine || item.material || item.meterialName || "",
          productType:
            item.productType?.join(", ") || item.rentalType?.join(", ") || "",
          region: item.regionId || "",
          rentPricePerDay: item.rentPricePerDay || null,
          businessType: businessType,
        }));

        setProducts(normalized);
        // Extract unique regions

        // Only update filtered products if no filters are active
        if (!brand && !model) {
          setFilteredProducts(normalized);
        }

        setPagination({
          ...pagination,
          totalPages: paginationData.totalPages,
          totalItems: paginationData.totalItems,
        });

        // Set brands/models from relevant type
        const filters = typeDetailsRes.data[typeKey];

        if (filters) {
          setBrandOptions(filters.brands || []);
          setModelOptions(filters.models || []);
        } else {
          console.warn(`No filter data found for typeKey: ${typeKey}`);
          setBrandOptions([]);
          setModelOptions([]);
        }

        // Extract regions from products
        const uniqueRegions = [
          ...new Map(
            data
              .map((item) => item.regionId)
              .filter((r) => r && r._id)
              .map((r) => [r._id, r])
          ).values(),
        ];
        setRegionOptions(uniqueRegions);

        // Extract years from products
        const uniqueYears = [
          ...new Set(data.map((item) => item.year).filter(Boolean)),
        ];
        setYearOptions(uniqueYears);
      } catch (err) {
        console.error("Error fetching products or filter options:", err);
        setError("Failed to load products.");
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchProductsAndFilters();
  }, [pagination.currentPage, pagination.itemsPerPage, filtersChanged]); // Add filtersChanged as dependency

  // Filter products when brand or model changes
  useEffect(() => {
    let filtered = [...products];

    if (brand) {
      filtered = filtered.filter((product) => product.brand === brand);
    }

    if (model) {
      filtered = filtered.filter((product) => product.model === model);
    }

    if (region) {
      filtered = filtered.filter(
        (product) => product.region?._id === region || product.region === region
      );
    }

    if (year) {
      filtered = filtered.filter((product) => product.year === year);
    }

    setFilteredProducts(filtered);

    if (brand || model || region || year) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        totalPages: Math.ceil(filtered.length / prev.itemsPerPage),
        totalItems: filtered.length,
      }));
    }
  }, [brand, model, region, year, products]);


  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  // Handle filter changes
  const handleBrandChange = (e) => {
    setBrand(e.target.value);
    setModel("");
    setFiltersChanged((prev) => !prev); // Toggle to trigger refetch
  };

  const handleModelChange = (e) => {
    setModel(e.target.value);
    setFiltersChanged((prev) => !prev); // Toggle to trigger refetch
  };

  // if (loading) return <p>Loading products...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundImage: "url(/images/bnr-bg.png)",
  };
  return (

    <div className="New-Dashboard" style={sectionStyle}>
      <Helmet>
        <title>
          {brand || model
            ? `Spare Parts ${year ? `(${year})` : ""} | Asia Parts`
            : "Auto Spare Parts Online | Asia Parts"}
        </title>

        <meta
          name="description"
          content={
            brand || model
              ? `Find spare parts for selected brand and model. Filter by year and region.`
              : "Browse genuine auto spare parts by brand, model, region and year. Fast enquiry and trusted suppliers."
          }
        />
      </Helmet>


      <div className="Main-div">
        {/* <Header /> */}
        <TopNavBar />
        <div className="TopSearchBar">
          <TopSearchBar />
        </div>
        <section style={{ width: "100%" }}>
          <Container className="">
            <div className="d-flex justify-content-between align-items-center m-4 p-5">
              <h2 className="fw-bold">Our Products</h2>
              <div className="d-flex align-items-center">
                <span className="me-2 text-muted">Sort by:</span>
                <Form.Select size="sm" style={{ width: "180px" }}>
                  <option>Featured</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Customer Rating</option>
                </Form.Select>
              </div>
            </div>
            {/* Search and Filter Bar */}
            <div className="mb-4 p-3   rounded-3">
              <Row className="g-2">
                <Col md={2}>
                  <Form.Select value={brand} onChange={handleBrandChange}>
                    <option value="">Select Brand</option>
                    {(brandOptions || []).map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={2}>
                  <Form.Select value={model} onChange={handleModelChange}>
                    <option value="">Select Model</option>
                    {(modelOptions || [])
                      .filter((m) => !brand || m.brandId === brand)
                      .map((m) => (
                        <option key={m._id} value={m._id}>
                          {m.name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select
                    value={region}
                    onChange={(e) => {
                      setRegion(e.target.value);
                      setFiltersChanged((prev) => !prev);
                    }}
                  >
                    <option value="">Select Region</option>
                    {(regionOptions || []).map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={2}>
                  <Form.Select
                    value={year}
                    onChange={(e) => {
                      setYear(e.target.value);
                      setFiltersChanged((prev) => !prev);
                    }}
                  >
                    <option value="">Select Year</option>
                    {(yearOptions || []).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              {isLoading && (
                <div className="d-flex justify-content-center mt-3">
                  <Spinner animation="border" variant="danger" />
                </div>
              )}
            </div>

            {/* Product Grid */}
            <Row xs={1} md={2} lg={3} xl={5} className="g-4">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <Col key={product.id}>
                    <Card className="h-100 border-0 shadow-sm hover-shadow">
                      <div className="position-relative">
                        <Card.Img
                          variant="top"
                          src={product.images[0]?.url || "/images/no-image.jpg"}
                          alt={product.name}
                          onError={(e) => {
                            e.target.onerror = null; // prevents infinite loop if default image also fails
                            e.target.src = "/images/download.jpeg"; // path relative to public folder
                          }}
                          style={{ height: "200px", objectFit: "cover" }}
                        />
                      </div>
                      <Link
                        to={
                          product.businessType === "Auto-Spare"
                            ? `/singleproduct/${product.id}`
                            : product.businessType === "Engineering"
                              ? `/engineering/product/single/${product.id}`
                              : product.businessType === "Building"
                                ? `/others/product/single/${product.id}`
                                : product.businessType === "Rental"
                                  ? `/rental/product/single/${product.id}`
                                  : "#"
                        }
                        style={{
                          textDecoration: "none",
                          color: "#1A54A5",
                          fontWeight: "bolder",
                        }}
                      >
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="fs-6 mb-2">
                            {/* {product.name} 
                        {product.partNo}  */}
                            <div class=" ">
                              <h5>{product?.name}</h5>
                              <div class="d-flex flex-row">
                                <div class="text-danger mb-1 me-2">
                                  <i class="fa fa-star"></i>
                                  <i class="fa fa-star"></i>
                                  <i class="fa fa-star"></i>
                                </div>
                                {/* <span>310</span> */}
                              </div>
                              <div className="mt-1 mb-0 text-muted small">
                                <span>{product.partNo}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.year}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.engine}</span>
                              </div>
                              <div className="mb-2 text-muted small">
                                <span>{product.productType}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.region?.name}</span>
                              </div>
                            </div>
                          </Card.Title>
                        </Card.Body>
                      </Link>
                    </Card>
                  </Col>
                ))
              ) : (
                !isLoading && (
                  <Col xs={12} className="text-center py-5">
                    <h4>No products found matching your filters</h4>
                  </Col>
                )
              )}
            </Row>
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <nav>
                  <ul className="pagination">
                    {/* Previous Button */}
                    <li
                      className={`page-item ${pagination.currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage - 1)
                        }
                      >
                        Previous
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const { currentPage, totalPages } = pagination;

                      const startPage = Math.max(2, currentPage - 2);
                      const endPage = Math.min(totalPages - 1, currentPage + 2);

                      // Always show first page
                      pages.push(
                        <li
                          key={1}
                          className={`page-item ${currentPage === 1 ? "active" : ""
                            }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </button>
                        </li>
                      );

                      // Left ellipsis
                      if (startPage > 2) {
                        pages.push(
                          <li
                            key="start-ellipsis"
                            className="page-item disabled"
                          >
                            <span className="page-link">...</span>
                          </li>
                        );
                      }

                      // Dynamic middle pages
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <li
                            key={i}
                            className={`page-item ${currentPage === i ? "active" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      // Right ellipsis
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <li key="end-ellipsis" className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(
                          <li
                            key={totalPages}
                            className={`page-item ${currentPage === totalPages ? "active" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}

                    {/* Next Button */}
                    <li
                      className={`page-item ${pagination.currentPage === pagination.totalPages
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage + 1)
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </Container>
        </section>
      </div>
    </div>
  );
}

export default Products;
